import { Dispatch, useCallback, useEffect, useRef, useState } from 'react'
import {
  ArticlePostPreviewAction,
  ArticlePostPreviewActionTypes,
  providerFromChannelType,
} from './ArticlePostPreviewContext'
import { AlmanacFileSummary, UserRole } from 'types'
import { UseQueryResult } from 'react-query'
import { ExternalIntegration, ExternalIntegrationEnum } from 'api/clients/ExternalIntegrationsClient'
import { ChannelType } from 'components/CampaignCreationModal/Types'
import { useUpdateScheduledPost } from 'hooks/queries/scheduledPosts/useUpdateScheduledPost'
import { useCurrentWorkspaceId } from 'hooks/useCurrentWorkspaceId'
import { AssetType, AssigneeTypeFromAssetType } from 'hooks/useAlmanacFileSubscription'
import { useScheduledPost } from 'hooks/queries/scheduledPosts/useScheduledPost'
import { CreateScheduledPostParams, ScheduledPostShow } from 'api/clients/ScheduledPostsClient'
import { useIsChannelPostable } from 'components/PostStatusDropdown/utils'
import { useRootStore } from 'stores/RootStore'
import { usePostProperties } from './usePostProperties'
import { PostStatusPropertyValue } from 'api/clients/PropertyClient'
import { useExternalIntegrations } from 'components/ExportModal/ExternalIntegrations/ExternalIntegrations'
import { useBlogIntegrations } from 'hooks/useBlogIntegrations'
import { useOptionalDocStore } from 'stores/DocStore'
import useResponsiveDesign from 'hooks/useResponsiveDesign'
import { useCreateScheduledPost } from 'hooks/queries/scheduledPosts/useCreateScheduledPost'
import { useWorkspace } from 'hooks/useWorkspace'
import { reportError } from 'utils/error'

export const useCreateOrUpdateScheduledPost = ({
  provider,
  almanacFile,
}: {
  provider: ExternalIntegrationEnum | undefined
  almanacFile?: AlmanacFileSummary | Omit<AlmanacFileSummary, 'ancestors'>
}) => {
  const createMutation = useCreateScheduledPost(provider, { suppressToast: true })
  const updateMutation = useUpdateScheduledPost({ suppressToast: true })
  const workspaceId = useCurrentWorkspaceId()

  const createOrUpdateScheduledPost = async (params: Partial<CreateScheduledPostParams>) => {
    if (almanacFile?.scheduled_post_id) {
      return updateMutation.mutateAsync({ id: almanacFile.scheduled_post_id, params })
    }
    return createMutation.mutateAsync({
      workspaceId,
      params: {
        sourceId: almanacFile?.id,
        sourceType: almanacFile?.asset_type,
        status: 'draft',
        ...params,
      } as CreateScheduledPostParams,
    })
  }

  return { createOrUpdateScheduledPost }
}

interface UseInitializeContextProps {
  dispatch: Dispatch<ArticlePostPreviewAction>
  almanacFile: AlmanacFileSummary
  initialized: boolean
}

export const useSetExternalIntegration = ({
  scheduledPost,
  integrationsQuery,
  provider,
}: {
  scheduledPost: ScheduledPostShow | undefined
  integrationsQuery: UseQueryResult<{ external_integrations: ExternalIntegration[] }>
  provider?: ExternalIntegrationEnum
}) => {
  const updateMutation = useUpdateScheduledPost({ suppressToast: true })
  const hasSetIntegrationRef = useRef(false)
  const { blogIntegrations } = useBlogIntegrations()

  useEffect(() => {
    if (hasSetIntegrationRef.current || !scheduledPost || scheduledPost.external_integration) {
      return
    }

    let integrationToSelect: ExternalIntegration | undefined

    const matchingIntegration = integrationsQuery.data?.external_integrations.find((i) => i.provider === provider)

    if (matchingIntegration) {
      integrationToSelect = matchingIntegration
    } else if (provider === ExternalIntegrationEnum.WordPress && blogIntegrations.length === 1) {
      integrationToSelect = blogIntegrations[0]
    }

    if (!integrationToSelect) return

    hasSetIntegrationRef.current = true

    updateMutation
      .mutateAsync({
        id: scheduledPost.id,
        params: {
          externalIntegrationId: integrationToSelect.id,
        },
      })
      .catch((error) => {
        reportError(error, { metaData: { scheduledPostId: scheduledPost.id } })
      })
  }, [scheduledPost, integrationsQuery.data, provider, updateMutation, blogIntegrations])
}

export const useAuthedArticle = (almanacFile: Omit<AlmanacFileSummary, 'ancestors'>) => {
  const articleWorkspace = useWorkspace(almanacFile.workspace.id || null)

  return {
    role: articleWorkspace?.role || UserRole.UNAUTHED,
    permissions: almanacFile.permissions,
    __caslSubjectType__: 'Article',
  }
}

export const useInitializeContext = ({ dispatch, almanacFile, initialized }: UseInitializeContextProps) => {
  const [content, setContent] = useState<string | null>(null)
  const { data: scheduledPost, isFetched, refetch } = useScheduledPost({ id: almanacFile.scheduled_post_id })
  const { designStore } = useRootStore()
  const { isMobile } = useResponsiveDesign()
  const { status } = usePostProperties({ id: almanacFile.id, type: AssigneeTypeFromAssetType[almanacFile.asset_type] })
  const integrationsQuery = useExternalIntegrations()
  const docStore = useOptionalDocStore()
  const [loading, setLoading] = useState(false)
  const provider = providerFromChannelType[almanacFile.channel as ChannelType]
  const { isChannelPostable } = useIsChannelPostable()
  useSetExternalIntegration({ scheduledPost, integrationsQuery, provider })

  const dispatchEmptyInitialize = useCallback(() => {
    dispatch({
      type: ArticlePostPreviewActionTypes.INITIALIZE,
      payload: {
        files: [],
        initialContent: '',
        initialFiles: [],
        errorMessage: '',
        content: '',
        integration: undefined,
        loading: false,
        editing: false,
        changingCopy: false,
        newCopy: '',
        oldCopy: '',
        uploadingMediaFilesMetadata: [],
      },
    })
  }, [dispatch])

  const dispatchLoadingState = useCallback(() => {
    setLoading(true)
    dispatch({
      type: ArticlePostPreviewActionTypes.SET_LOADING,
      payload: true,
    })
  }, [dispatch])

  const dispatchFinalInitialize = useCallback(async () => {
    if (almanacFile.scheduled_post_id && !isFetched) {
      return // Wait for the initial fetch to complete
    }

    const res = await refetch()
    const post = res.data
    const integrations = integrationsQuery.data?.external_integrations || []
    const integration =
      post?.external_integration ||
      integrations.find((i) => i.provider === providerFromChannelType[almanacFile.channel as ChannelType])

    if (almanacFile.scheduled_post_id && !post) {
      return
    }
    const finalContent = post?.content || ''

    dispatch({
      type: ArticlePostPreviewActionTypes.INITIALIZE,
      payload: {
        files: post?.media_objects || [],
        initialContent: finalContent,
        initialFiles: post?.media_objects || [],
        errorMessage: post?.error_message,
        errorType: post?.error_type,
        content: finalContent,
        integration,
        status: { id: status.id, value: status.value },
        loading: false,
        editing: !isMobile && status?.value === PostStatusPropertyValue.Draft,
        changingCopy: false,
        newCopy: '',
        oldCopy: '',
        channel: almanacFile.channel as ChannelType,
        uploadingMediaFilesMetadata: [],
      },
    })
  }, [dispatch, integrationsQuery.data, almanacFile, refetch, status, isMobile, isFetched])

  useEffect(() => {
    if (almanacFile.asset_type === AssetType.Design) {
      setContent(designStore?.captionsStore?.caption || ' ')
    }
  }, [almanacFile, designStore])

  useEffect(() => {
    if (initialized) return

    const initialize = async () => {
      const isChannelPostableResult = isChannelPostable(almanacFile.channel as ChannelType)
      if (!isChannelPostableResult) {
        dispatchEmptyInitialize()
        return
      }

      const scheduledPostReady = almanacFile.scheduled_post_id ? isFetched : true

      if (!integrationsQuery.isFetched || !scheduledPostReady || docStore?.isGeneratingSuggestion === undefined) {
        return // Wait for all data to be fetched
      }

      if (docStore?.isGeneratingSuggestion) {
        if (!loading) dispatchLoadingState()
      } else {
        dispatchFinalInitialize()
      }
    }

    initialize()
  }, [
    integrationsQuery,
    content,
    dispatch,
    initialized,
    almanacFile,
    isFetched,
    scheduledPost,
    status,
    isMobile,
    docStore?.isGeneratingSuggestion,
    loading,
    isChannelPostable,
    dispatchEmptyInitialize,
    dispatchLoadingState,
    dispatchFinalInitialize,
  ])

  return initialized
}

export default useInitializeContext
